import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { DeleteIcon } from '@/components/molecules/settings/icon/delete-icon'
import { AssetType } from '@/models/guest-app/asset'
import { ContainerHeader } from '@/components/molecules/settings/container-header'
import { ContainerSidePanelBody, Props as SidePanelProps } from '@/components/molecules/settings/container-side-panel-body'
import { AssetDetailContext } from '@/components/pages/setting/guest-app/asset-detail'
import { deleteMobileRequestSalesHours, updateAsset, updateAssetRequestSalesHourDisplayOrder } from '@/apis/aipass'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { useDraggableDom } from '@/components/molecules/settings/use-draggable-dom'
import { SalesHourType } from '@/models/mobile-request'
import { RequestSalesHourDetailModal } from './request-sales-hour-detail-modal'
import { GuestAppTableHeader, GuestAppTableBody, GuestAppTableRow } from '@/components/atoms/settings/guest-app-table'

type Props = {
  asset: AssetType & { salesHours: SalesHourType[] }
}

export const RequestSalesHourList: React.FC<Props & SidePanelProps> = ({ asset, menus }) => {
  const { t } = useTranslation()
  const [formValue, setFormValue] = useState<{ title: string; salesHours: SalesHourType[] }>({
    title: asset.title,
    salesHours: asset.salesHours,
  })
  const [deleteIds, setDeleteIds] = useState<number[]>([])
  const [showDetailProp, setShowDetailProp] = useState<SalesHourType | undefined | null>(undefined)
  const { doReload, setIsLoading, setIsEditing, listenDoSave, listenDoCancel } = useContext(AssetDetailContext)
  const { errorHandler } = useErrorHandler()
  const addDeleteTarget = (id: number) => {
    setFormValue({ ...asset, salesHours: formValue.salesHours.filter(detail => detail.id !== id) })
    setDeleteIds([...deleteIds, id])
    setIsEditing(true)
  }
  const changeDisplayOrder = (newList: SalesHourType[]) => {
    setFormValue({ ...asset, salesHours: newList })
    setIsEditing(true)
  }
  const { dragStart, dragOver, dragDrop, dragEnd, handleTouchStart, handleTouchMove, handleTouchEnd } = useDraggableDom({
    list: formValue.salesHours,
    onChange: changeDisplayOrder,
  })

  const saveFormValue = async () => {
    try {
      setIsLoading(true)
      if (deleteIds.length) {
        await deleteMobileRequestSalesHours(deleteIds)
        setDeleteIds([])
      }
      if (formValue.salesHours.length) {
        await updateAssetRequestSalesHourDisplayOrder(
          asset.id,
          formValue.salesHours.map(detail => detail.id),
        )
      }
      await updateAsset(asset.id, { title: formValue.title })
      doReload()
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (listenDoSave) saveFormValue()
  }, [listenDoSave])

  useEffect(() => {
    if (listenDoCancel) {
      setDeleteIds([])
      doReload()
    }
  }, [listenDoCancel])

  useEffect(() => {
    if (!asset) {
      return
    }
    setFormValue({
      title: asset.title,
      salesHours: asset.salesHours,
    })
  }, [asset])

  return (
    <>
      <ContainerHeader
        goBack
        title={formValue.title}
        onChangeTitle={(newTitle: string) => {
          setFormValue({ ...formValue, title: newTitle })
          setIsEditing(true)
        }}
        onClickAddButton={() => setShowDetailProp(null)}
      />
      <ContainerSidePanelBody menus={menus}>
        <>
          <GuestAppTableHeader>
            <div css={titleColStyle}>{t('Title')}</div>
            <div css={hourColStyle}>{t('MobileRequest.Sales hours')}</div>
            <div css={deleteColStyle}></div>
          </GuestAppTableHeader>
          <GuestAppTableBody>
            {formValue.salesHours.map(detail => (
              <GuestAppTableRow
                id={detail.id.toString()}
                key={detail.id}
                onClick={() => setShowDetailProp(detail)}
                draggable="true"
                onDragStart={dragStart}
                onDragOver={dragOver}
                onDrop={dragDrop}
                onDragEnd={dragEnd}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                <div css={titleColStyle}>{detail.name}</div>
                <div css={hourColStyle}>{detail.hours.map(hour => `${hour.from}-${hour.to}`).join(',')}</div>
                <div css={deleteColStyle}>
                  <DeleteIcon onClick={() => addDeleteTarget(detail.id)} />
                </div>
              </GuestAppTableRow>
            ))}
          </GuestAppTableBody>
        </>
      </ContainerSidePanelBody>
      {showDetailProp !== undefined && (
        <RequestSalesHourDetailModal
          asset={asset}
          detail={showDetailProp}
          onCancel={() => setShowDetailProp(undefined)}
          onSave={() => setShowDetailProp(undefined)}
        />
      )}
    </>
  )
}

const titleColStyle = css({
  marginLeft: 62,
  width: 273,
})
const hourColStyle = css({
  marginLeft: 62,
  flexGrow: 1,
  overflowWrap: 'anywhere',
  width: 'min-content',
})
const deleteColStyle = css({
  width: 30,
  margin: '0 16px',
})
